/**
 * Baxter — the WordPuppi AI co-editor (AB#645 lean v1).
 *
 * REST lives under `/v1/sites/{slug}/ai`; the chat turn itself is an SSE stream
 * (see `streamChat`). Everything here follows the shared Baxter contract.
 */

import { api, apiStream, ApiError } from './client';

/** One MCP tool invocation as the panel displays it. */
export interface AiToolCall {
  id: string;
  name: string;
  args: unknown;
  status: 'ok' | 'error' | 'pending' | 'rejected';
  summary?: string;
  /** `wpp_render_url` stored its screenshot in site media (AB#642). */
  screenshot_url?: string | null;
  /** `wpp_build_site` — the site's shareable preview link (AB#673). */
  preview_url?: string | null;
}

/** A chat message as the backend projects it (and as the panel keeps it). */
export type UiMessage =
  | { role: 'user'; content: string; pinned_context?: AiPinnedContext[] }
  | { role: 'assistant'; content: string; tool_calls?: AiToolCall[] };

/** Row in the chat history list. */
export interface AiChatSummary {
  id: string;
  title: string;
  updated_at: string;
}

/** A current site fact turned into an actionable Baxter starter (AB#953). */
export interface AiImprovement {
  title: string;
  detail: string;
  prompt: string;
  href: string;
}

/** A loaded chat with its full message list. */
export interface AiChat {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  messages: UiMessage[];
  /** AB#669: the model this chat last used, if the backend projects it. */
  model?: string | null;
  /** AB#642: site-generator skill id this chat was started with. */
  skill?: string | null;
  pinned_context?: AiPinnedContext[];
}

/** An explicit site reference in the composer. The server resolves its id again. */
export interface AiPinnedContext {
  type: 'post' | 'page' | 'theme_file' | 'media' | 'selection';
  id: string;
  title: string;
  excerpt?: string;
}

/** AB#810: one element the person picked on the page canvas. */
export interface AiSelectionElement {
  /** Stable CSS path — `#id` when the element has one, else an nth-child path from the editable region. */
  selector: string;
  /** The element's own markup, as the SAVED page stores it, trimmed to 8 KB. */
  html: string;
}

/** AB#810: what the person had selected on the canvas when they asked Baxter. */
export interface AiSelectionContext {
  kind: 'page';
  page_id: string;
  page_slug: string;
  /** AB#832: a richtext page is rendered into the canvas, so its ids are canvas-only. */
  editor_mode?: 'designed' | 'richtext';
  /** Device the canvas was showing, e.g. `Phone · 600`. */
  breakpoint: string;
  scheme: 'light' | 'dark';
  elements: AiSelectionElement[];
  /** The page CSS rules that target those elements (by id). */
  css: string;
  /** Measured px width of the content column the selection sits in. */
  container_width?: number;
  /** The theme's own `:root{…}` custom properties, read off the canvas. */
  root_css?: string;
}

/** Implicit route context sent with every turn — all fields optional. */
export interface AiChatContext {
  pinned_context?: AiPinnedContext[];
  /** Device/calendar IANA zone for resolving local dates; not an authorization signal. */
  device_timezone?: string;
  route?: string;
  post_id?: string;
  page_id?: string;
  theme_file?: string;
  bp?: string;
  /** AB#642: skill id, mirrored for the model's benefit. */
  skill?: string;
  /**
   * AB#642: media the person uploaded in the New Site wizard (site-relative url
   * strings); AB#949: Baxter composer attachments as media-library objects.
   */
  uploaded_media?: (
    | string
    | { id?: number | string; filename: string; url: string; mime_type?: string }
  )[];
  /** AB#810: the canvas selection this turn is about. */
  selection?: AiSelectionContext;
}

/** AB#669: free tool calls on WordPuppi's own OpenRouter key. */
export interface AiUsage {
  used: number;
  limit: number;
  /** true when the caller has an OpenRouter key of their own (never counted). */
  byok: boolean;
  monthly: {
    month: string;
    platform_tokens_used: number;
    platform_tokens_provider_reported: number;
    platform_budget_overrun: boolean;
    platform_tokens_limit: number;
    platform_tokens_remaining: number;
    byok_tokens_reported: number;
    known_estimated_cost_microusd: number;
    incomplete_turns: number;
    turns: Array<{
      turn_id: string;
      site_id: number;
      chat_id: string;
      provider: string;
      model: string;
      funding: 'platform' | 'byok';
      prompt_tokens: number;
      completion_tokens: number;
      usage_complete: boolean;
      estimated_cost_microusd: number | null;
      created_at: number;
    }>;
  };
}

/** AB#642: one Baxter skill (Love, Based, Vee, Bolt, Port, SEO). */
export interface AiSkill {
  id: string;
  name: string;
  tagline: string;
  /** AB#640: builds a new site, so the wizard offers it. `false` (SEO) works on an existing site. */
  generator?: boolean;
}

/** POST /ai/chat body. `message` is null on an approval round-trip. */
export interface AiChatRequest {
  chat_id?: string | null;
  message?: string | null;
  approval?: { tool_call_id: string; approved: boolean } | null;
  context?: AiChatContext;
  /** AB#669: model id for this turn — defaults server-side to `openrouter/free`. */
  model?: string | null;
  /** AB#642: skill id for a NEW chat (ignored on later turns; the chat row keeps it). */
  skill?: string | null;
}

/** A decoded SSE event: `chat` | `model` | `delta` | `tool_start` | `tool_result` | `approval_required` | `done` | `error`. */
export interface SseEvent {
  event: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

/**
 * ponytail: the contract returns bare JSON for the REST endpoints, but every
 * other wpp controller wraps in `{ success, data }`. Accept either instead of
 * betting the panel on which one the backend actually ships.
 */
function unwrap<T>(res: unknown): T {
  const envelope = res as { success?: boolean; data?: T } | null;
  if (envelope && typeof envelope === 'object' && 'data' in envelope && 'success' in envelope) {
    return envelope.data as T;
  }
  return res as T;
}

/** Parse one `event:`/`data:` block (blank-line delimited) into an SseEvent. */
function parseEventBlock(raw: string): SseEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue; // blank or comment/keep-alive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  if (!data.length) return null;
  const text = data.join('\n');
  try {
    return { event, data: JSON.parse(text) };
  } catch {
    return { event, data: text };
  }
}

/**
 * Stateful SSE splitter: feed it decoded chunks (which cut events anywhere,
 * including mid-line), get back whole events. Pure + exported so it can be
 * tested without a network.
 */
export function createSseParser(maxBufferedCharacters = Infinity): (chunk: string) => SseEvent[] {
  let buffer = '';
  return (chunk: string) => {
    buffer += chunk;
    if (buffer.length > maxBufferedCharacters) throw new Error('SSE frame exceeds size limit');
    const events: SseEvent[] = [];
    for (;;) {
      const boundary = /\r?\n\r?\n/.exec(buffer);
      if (!boundary) break;
      const block = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary[0].length);
      const parsed = parseEventBlock(block);
      if (parsed) events.push(parsed);
    }
    return events;
  };
}

export const aiApi = {
  /** AB#669: free-tool-call budget for the caller (site_slug resolves site/account keys). */
  usage: (siteSlug?: string) =>
    api
      .get<unknown>(`/v1/ai/usage${siteSlug ? `?site_slug=${encodeURIComponent(siteSlug)}` : ''}`)
      .then((res) => unwrap<AiUsage>(res)),

  /** AB#642: the site-generator skills the wizard offers. */
  listSkills: () => api.get<unknown>('/v1/ai/skills').then((res) => unwrap<AiSkill[]>(res) ?? []),

  /** Chat history, newest first (max 50). */
  listChats: (siteSlug: string) =>
    api
      .get<unknown>(`/v1/sites/${siteSlug}/ai/chats`)
      .then((res) => unwrap<AiChatSummary[]>(res) ?? []),

  improvements: (siteSlug: string) =>
    api.get<unknown>(`/v1/sites/${siteSlug}/ai/improvements`)
      .then((res) => unwrap<AiImprovement[]>(res) ?? []),

  /** One chat with its messages. */
  getChat: (siteSlug: string, id: string) =>
    api.get<unknown>(`/v1/sites/${siteSlug}/ai/chats/${id}`).then((res) => unwrap<AiChat>(res)),

  /** Delete a chat (204). */
  deleteChat: (siteSlug: string, id: string) =>
    api.delete<void>(`/v1/sites/${siteSlug}/ai/chats/${id}`),

  /** AB#834: delete every chat the caller owns on this site (204). */
  clearChats: (siteSlug: string) => api.delete<void>(`/v1/sites/${siteSlug}/ai/chats`),

  /**
   * Run one chat turn. Yields SSE events until `done`, `error`, or
   * `approval_required` (after which the caller re-POSTs with `approval`).
   * Aborting the signal ends the generator — on desktop plugin-http rejects
   * with a plain `Error('Request cancelled')` rather than an AbortError, so
   * callers should treat `signal.aborted` as the cancel test, not the error type.
   */
  async *streamChat(
    siteSlug: string,
    body: AiChatRequest,
    signal?: AbortSignal,
    streamFetch?: typeof fetch,
  ): AsyncGenerator<SseEvent> {
    const response = await apiStream(`/v1/sites/${siteSlug}/ai/chat`, body, signal, streamFetch);
    const reader = response.body?.getReader();
    if (!reader) {
      throw new ApiError(0, { error: 'Streaming responses are not supported in this browser.' });
    }

    const decoder = new TextDecoder();
    const feed = createSseParser();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const event of feed(decoder.decode(value, { stream: true }))) yield event;
      }
      // Flush a final event the server didn't terminate with a blank line.
      for (const event of feed('\n\n')) yield event;
    } finally {
      reader.cancel().catch(() => {});
    }
  },
};
